import * as yup from "yup";
import { formatDateRange } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import type {
  EnrichedIcmStatement,
  EnrichedIcmStatementDetail,
} from "@/icm/types";
import type { PeriodOption } from "./types";

const MAX_FEEDBACK_INQUIRY_LENGTH = 1500;

export const sortPayPeriodsDesc = <T extends { start_date: string }>(
  periods: T[],
) => [...periods].sort((a, b) => b.start_date.localeCompare(a.start_date));

export const getStatementOwnerKey = (
  statement?: Pick<EnrichedIcmStatement, "technician_crm_external_id"> | null,
) => statement?.technician_crm_external_id ?? null;

export const isStatementOwnedByTech = (
  statement?: Pick<EnrichedIcmStatement, "technician_crm_external_id"> | null,
  crmExternalId?: string | null,
) =>
  !!statement &&
  !!crmExternalId &&
  getStatementOwnerKey(statement) === crmExternalId;

export const getTechnicianDisplayName = (
  techUser?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null,
) => {
  if (!techUser) return "Your Statement";

  const fullName = [techUser.first_name, techUser.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || techUser.email;
};

export const toPeriodOption = (period: {
  start_date: string;
  end_date: string;
}): PeriodOption => ({
  value: period.start_date,
  label: formatDateRange(period.start_date, period.end_date),
});

export const buildFeedbackInquiry = ({
  inquiry,
  statement,
  businessName,
}: {
  inquiry: string;
  statement: EnrichedIcmStatementDetail | null;
  businessName?: string;
}) => {
  if (!statement) return inquiry;

  return [
    inquiry.trim(),
    "",
    "---",
    "Statement context",
    `Business: ${businessName ?? "–"}`,
    `Statement ID: ${statement.id}`,
    `Pay Period: ${formatDateRange(statement.period_start, statement.period_end)}`,
    `Technician CRM External ID: ${statement.technician_crm_external_id ?? "–"}`,
    `Total pay: ${formatMoney(statement.total_payout) ?? statement.total_payout}`,
  ].join("\n");
};

export const getFeedbackValidationSchema = ({
  statement,
  businessName,
}: {
  statement: EnrichedIcmStatementDetail | null;
  businessName?: string;
}) =>
  yup.object({
    inquiry: yup
      .string()
      .required("Feedback is required")
      .min(10, "Feedback must be at least 10 characters")
      .max(
        MAX_FEEDBACK_INQUIRY_LENGTH,
        "Feedback must be less than 1500 characters",
      )
      .test(
        "complete-inquiry-length",
        "Feedback is too long once statement details are attached. Please shorten your message.",
        (value) =>
          buildFeedbackInquiry({
            inquiry: value ?? "",
            statement,
            businessName,
          }).length <= MAX_FEEDBACK_INQUIRY_LENGTH,
      ),
  });
